/**
 * Weekly Review tracked keyword preferences.
 *
 * Source of truth is GET/PUT /api/opportunities/keyword-watchlist (project-scoped).
 * localStorage `rfp-weekly-review-tracked-keywords-v1:{projectId}:{userId}` is a
 * one-time seed when the server row is still empty, then a client cache.
 */

import type {
  KeywordWatchlistResponse,
  RecommendedKeywordsResponse,
} from '../api/opportunities';
import { normalizeMonitorKeyword } from './alertKeywordRecommendations';
import { extractWeeklyReviewKeywords } from './weeklyGlobalOpportunities';

export const WEEKLY_REVIEW_TRACKED_KEYWORDS_STORAGE_PREFIX =
  'rfp-weekly-review-tracked-keywords-v1';

export interface WeeklyReviewTrackedKeywordsPrefs {
  selectedKeywords: string[];
  updatedAt?: string;
}

export interface WeeklyReviewKeywordCatalogEntry {
  keyword: string;
  confidencePct?: number;
  isCustom?: boolean;
}

export function getWeeklyReviewTrackedKeywordsStorageKey(
  projectId: string,
  userId: string
): string {
  return `${WEEKLY_REVIEW_TRACKED_KEYWORDS_STORAGE_PREFIX}:${projectId}:${userId}`;
}

function isValidPrefs(value: unknown): value is WeeklyReviewTrackedKeywordsPrefs {
  if (!value || typeof value !== 'object') return false;
  const record = value as WeeklyReviewTrackedKeywordsPrefs;
  return Array.isArray(record.selectedKeywords);
}

export function loadWeeklyReviewTrackedKeywords(
  projectId: string | undefined,
  userId: string | undefined
): WeeklyReviewTrackedKeywordsPrefs | null {
  if (!projectId || !userId || typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(getWeeklyReviewTrackedKeywordsStorageKey(projectId, userId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidPrefs(parsed)) return null;
    return {
      selectedKeywords: parsed.selectedKeywords
        .map((keyword) => (typeof keyword === 'string' ? keyword.trim() : ''))
        .filter(Boolean),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : undefined,
    };
  } catch {
    return null;
  }
}

export function prefsFromWatchlistResponse(
  payload: KeywordWatchlistResponse | null | undefined
): WeeklyReviewTrackedKeywordsPrefs {
  return {
    selectedKeywords: dedupeKeywordList(payload?.selected_keywords ?? []),
    updatedAt: payload?.updated_at ?? undefined,
  };
}

export function saveWeeklyReviewTrackedKeywords(
  projectId: string,
  userId: string,
  prefs: WeeklyReviewTrackedKeywordsPrefs
): void {
  if (typeof localStorage === 'undefined') return;

  const payload: WeeklyReviewTrackedKeywordsPrefs = {
    selectedKeywords: dedupeKeywordList(prefs.selectedKeywords),
    updatedAt: prefs.updatedAt ?? new Date().toISOString(),
  };
  localStorage.setItem(
    getWeeklyReviewTrackedKeywordsStorageKey(projectId, userId),
    JSON.stringify(payload)
  );
}

export function dedupeKeywordList(keywords: string[]): string[] {
  const seen = new Set<string>();
  const results: string[] = [];

  for (const keyword of keywords) {
    const trimmed = keyword.trim();
    if (!trimmed) continue;
    const key = normalizeMonitorKeyword(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    results.push(trimmed);
  }

  return results;
}

/** Full recommended catalog for the picker (AI diversity order, no cap). */
export function buildWeeklyReviewKeywordCatalog(
  data: Pick<RecommendedKeywordsResponse, 'keywords' | 'service_keyword_groups'> | null | undefined,
  extraKeywords: string[] = []
): WeeklyReviewKeywordCatalogEntry[] {
  const ordered = extractWeeklyReviewKeywords(data);
  const confidenceByKey = new Map<string, number>();

  for (const entry of data?.keywords ?? []) {
    if (typeof entry.keyword !== 'string') continue;
    confidenceByKey.set(normalizeMonitorKeyword(entry.keyword), entry.confidence_pct ?? 0);
  }

  const catalogKeys = new Set<string>();
  const catalog: WeeklyReviewKeywordCatalogEntry[] = [];

  const addEntry = (keyword: string, isCustom = false) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    const key = normalizeMonitorKeyword(trimmed);
    if (catalogKeys.has(key)) return;
    catalogKeys.add(key);
    catalog.push({
      keyword: trimmed,
      confidencePct: confidenceByKey.get(key),
      isCustom,
    });
  };

  for (const keyword of ordered) {
    addEntry(keyword, false);
  }

  for (const keyword of extraKeywords) {
    const trimmed = keyword.trim();
    if (!trimmed) continue;
    const key = normalizeMonitorKeyword(trimmed);
    if (catalogKeys.has(key)) continue;
    catalogKeys.add(key);
    catalog.push({
      keyword: trimmed,
      confidencePct: confidenceByKey.get(key),
      isCustom: true,
    });
  }

  return catalog;
}

/**
 * Keywords used for Weekly Review searches.
 * Empty saved selection falls back to all recommended keywords above the confidence floor.
 */
export function resolveWeeklyReviewTrackedKeywords(
  saved: WeeklyReviewTrackedKeywordsPrefs | null | undefined,
  data: Pick<RecommendedKeywordsResponse, 'keywords' | 'service_keyword_groups'> | null | undefined
): string[] {
  const fallback = extractWeeklyReviewKeywords(data);
  const savedSelection = dedupeKeywordList(saved?.selectedKeywords ?? []);
  return savedSelection.length > 0 ? savedSelection : fallback;
}

export function mergeWeeklyReviewDraftSelection(
  catalog: WeeklyReviewKeywordCatalogEntry[],
  draftSelected: string[]
): string[] {
  const selectedKeys = new Set(draftSelected.map(normalizeMonitorKeyword));
  const ordered = catalog
    .map((entry) => entry.keyword)
    .filter((keyword) => selectedKeys.has(normalizeMonitorKeyword(keyword)));

  for (const keyword of draftSelected) {
    const trimmed = keyword.trim();
    if (!trimmed) continue;
    const key = normalizeMonitorKeyword(trimmed);
    if (ordered.some((existing) => normalizeMonitorKeyword(existing) === key)) continue;
    ordered.push(trimmed);
  }

  return dedupeKeywordList(ordered);
}

export function buildWeeklyReviewDraftSelection(
  catalog: WeeklyReviewKeywordCatalogEntry[],
  saved: WeeklyReviewTrackedKeywordsPrefs | null | undefined,
  data: Pick<RecommendedKeywordsResponse, 'keywords' | 'service_keyword_groups'> | null | undefined
): string[] {
  const savedSelection = dedupeKeywordList(saved?.selectedKeywords ?? []);
  if (savedSelection.length > 0) {
    return mergeWeeklyReviewDraftSelection(catalog, savedSelection);
  }
  return catalog.map((entry) => entry.keyword);
}
